import { useState, useEffect, useCallback, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { endpoint } from "../../api/endpoint";
import { apiHandler } from "../../api";

const DEBOUNCE_DELAY = 500; // ms

export const useOrders = () => {
  const dispatch = useDispatch();
  const { authData, storeId } = useSelector((state) => state.loginSlice);
  const [searchParams, setSearchParams] = useSearchParams();

  const pageFromUrl = parseInt(searchParams.get("page"), 10);
  const initialPage = !isNaN(pageFromUrl) && pageFromUrl > 0 ? pageFromUrl : 1;
  const limitFromUrl = parseInt(searchParams.get("limit"), 10);
  const initialLimit = !isNaN(limitFromUrl) && limitFromUrl > 0 ? limitFromUrl : 10;

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [itemsPerPage, setItemsPerPage] = useState(initialLimit);

  const [filters, setFilters] = useState({
    start_date: "",
    end_date: "",
    order_status: "",
    bap_id: "",
    domain: "",
    seller_np_order_id: "",
    network_order_id: "",
    customer_mobile: "",
    customer_email: "",
    customer_name: "",
    settlement_status: "",
    sort_by: "createdAt",
    sort_direction: "DESC",
    search_by: "",
    search_value: "",
    customer_state:"",
  });

  const [filterOptions, setFilterOptions] = useState({
    bap_ids: [],
    order_statuses: [],
    settlement_statuses: [],
    domains: [],
    states: [],
  });

  const debounceTimer = useRef(null);

  const getData = useCallback(async () => {
    try {
      setLoading(true);

      let url = endpoint.GET_ORDERS;
      const params = new URLSearchParams();

      // 1. Process standard filters
      Object.entries(filters).forEach(([key, value]) => {
        if (value && value.toString().trim() !== "") {
          const cleanValue = value.toString().trim();
          // Skip search keys here as they are handled together below
          if (key === "search_value" || key === "search_by") return;
          params.append(key, cleanValue);
        }
      });

      // 2. Process search override
      if (filters.search_by && filters.search_value) {
        params.append(filters.search_by, filters.search_value.trim());
      }

      // 3. Process Pagination
      params.append("page", currentPage);
      params.append("limit", itemsPerPage);

      if (params.toString()) {
        url += `?${params.toString()}`;
      }

      const result = await apiHandler({
        url,
        method: "GET",
        headers: { "Content-Type": "application/json" },
        authToken: authData,
        dispatch,
        storeId,
      });

      const apiResponseData = result?.data;

      if (apiResponseData?.data) {
        setData(apiResponseData.data.data || []);
        setTotalCount(apiResponseData.data.totalCount || 0);
      } else {
        setData([]);
        setTotalCount(0);
      }

      // Update filter dropdown options from API response
      if (apiResponseData?.filters) {
        setFilterOptions({
          bap_ids: apiResponseData.filters.bapData || [],
          order_statuses: apiResponseData.filters.orderStatus || [],
          settlement_statuses: apiResponseData.filters.settlementStatus || [],
          domains: apiResponseData.filters.domainList || [],
          states: apiResponseData.filters.locations.map((location) => location.states) || [],
        });
      }
    } catch (err) {
      //console.error("Error fetching order data:", err);
      setData([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [filters, currentPage, itemsPerPage, authData, dispatch, storeId]);

  // Handle Fetching with Debounce
  useEffect(() => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);

    const isDebouncedFilter =
      filters.search_by &&
      filters.search_value &&
      ["customer_name", "customer_mobile", "network_order_id", "seller_np_order_id"].includes(
        filters.search_by
      );

    const delay = isDebouncedFilter ? DEBOUNCE_DELAY : 0;

    setLoading(true); 
    debounceTimer.current = setTimeout(() => {
      getData();
    }, delay);

    return () => clearTimeout(debounceTimer.current);
  }, [getData]);

  const totalPages = Math.ceil(totalCount / itemsPerPage);

  const handlePageChange = useCallback(
    (newPage) => {
      const pageNum = typeof newPage === "function" ? newPage(currentPage) : newPage;
      const validPage = Math.max(1, pageNum);
      setCurrentPage(validPage);
      const next = new URLSearchParams(window.location.search);
      if (validPage <= 1) {
        next.delete("page");
      } else {
        next.set("page", validPage.toString());
      }
      setSearchParams(next, { replace: true });
    },
    [currentPage, setSearchParams]
  );

  // Sync state if URL search params change
  useEffect(() => {
    const pageInUrl = parseInt(searchParams.get("page"), 10);
    const validPage = !isNaN(pageInUrl) && pageInUrl > 0 ? pageInUrl : 1;
    setCurrentPage((prev) => (prev !== validPage ? validPage : prev));

    const limitInUrl = parseInt(searchParams.get("limit"), 10);
    if (!isNaN(limitInUrl) && limitInUrl > 0) {
      setItemsPerPage((prev) => (prev !== limitInUrl ? limitInUrl : prev));
    }
  }, [searchParams]);

  // Wrapper for items per page to ensure page resets to 1
  const updateItemsPerPage = (newLimit) => {
    const limitNum = Number(newLimit);
    setItemsPerPage(limitNum);
    setCurrentPage(1);
    const next = new URLSearchParams(window.location.search);
    next.delete("page");
    if (limitNum === 10) {
      next.delete("limit");
    } else {
      next.set("limit", limitNum.toString());
    }
    setSearchParams(next, { replace: true });
  };

  return {
    data,
    loading,
    filters,
    setFilters,
    filterOptions,
    totalCount,
    currentPage,
    setCurrentPage: handlePageChange,
    itemsPerPage,
    setItemsPerPage: updateItemsPerPage, // Reset page logic included
    totalPages,
  };
};